import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageHeader from '@/components/PageHeader';
import LogoScroller from '@/components/LogoScroller';
import { Card, CardContent } from "@/components/ui/card";
import { Building, Users, Globe } from "lucide-react";

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>About Us - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content="Learn about AI World Congress, the premier AI conference in London, UK, Europe. Our mission, history, and vision for AI World Congress 2026." />
        <link rel="canonical" href="https://aiconference.london/about-us" />
        <meta property="og:title" content="About Us - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta property="og:description" content="Learn about AI World Congress, the premier AI conference in London, UK, Europe." />
        <meta property="og:url" content="https://aiconference.london/about-us" />
        <meta property="og:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Us - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta name="twitter:description" content="Learn about AI World Congress, the premier AI conference in London." />
        <meta name="twitter:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
      </Helmet>
      <Header />
      <PageHeader
        title=""
        subtitle=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
      />
      <div className="relative z-10 -mt-16">
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
            <div className="absolute w-full h-[40%] bottom-0 bg-gradient-to-t from-black to-transparent"></div>
            <div className="absolute w-full h-[500px] bottom-0 left-0 right-0">
              <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                <path 
                  fill="#000000" 
                  fillOpacity="1" 
                  d="M0,128L48,138.7C96,149,192,171,288,176C384,181,480,171,576,144C672,117,768,75,864,80C960,85,1056,139,1152,160C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  className="animate-pulse"
                  style={{ animationDuration: '15s' }}
                ></path>
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-white">About </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Us</span>
              </h1>
            </div>
          </div>
        </div>
      
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-300 mb-12 text-center">
              AI World Congress is the premier event bringing together industry leaders, 
              innovators, and visionaries to explore the cutting edge of artificial intelligence.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 animate-fade-in">
              <Card className="bg-purple-900/20 backdrop-blur-sm border-purple-500/30">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <Globe className="h-12 w-12 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Our Mission</h3>
                  <p className="text-gray-300">
                    To accelerate the responsible advancement and adoption of AI technologies
                    through knowledge sharing and collaboration.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-purple-900/20 backdrop-blur-sm border-purple-500/30">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <Building className="h-12 w-12 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Our History</h3>
                  <p className="text-gray-300">
                    Founded in 2015, we've grown to become the largest AI conference in Europe,
                    hosting over 500 attendees annually.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-purple-900/20 backdrop-blur-sm border-purple-500/30">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <Users className="h-12 w-12 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Our Team</h3>
                  <p className="text-gray-300">
                    A diverse group of AI enthusiasts, researchers, and industry experts
                    committed to creating an exceptional conference experience.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">What Sets Us Apart</h2>
              <div className="bg-gradient-to-br from-[#0A66C2]/30 via-[#0A66C2]/20 to-transparent backdrop-blur-sm p-8 rounded-lg border border-[#0A66C2]/20 animate-fade-in" style={{ animationDelay: "200ms" }}>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Industry-leading speakers from top AI companies and research institutions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Hands-on workshops and live demonstrations of cutting-edge AI technologies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Unparalleled networking opportunities with AI professionals from around the globe</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Focus on practical applications and real-world case studies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Commitment to diversity, inclusion and ethical AI development</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Our Vision for AI World Congress 2026</h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 mb-4">
                  AI World Congress 2026 aims to be the most comprehensive and impactful AI event to date. With the rapid acceleration of AI technologies, we're curating content that addresses both the immense opportunities and important challenges facing the industry.
                </p>
                <p className="text-gray-300 mb-4">
                  Our program features expert sessions on generative AI, machine learning operations, AI ethics, regulatory frameworks, and industry-specific applications. Through thought-provoking keynotes, interactive panel discussions, and specialized workshops, attendees will gain valuable insights and practical knowledge to implement in their own organizations.
                </p>
                <p className="text-gray-300">
                  We invite you to join us in London for this transformative event and be part of shaping the future of artificial intelligence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LogoScroller />
      <Footer />
    </div>
  );
};

export default AboutUs;
